import BuyMeCoffeeButton from '../Coffee/BuyMeCoffeeButton';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  IdolStash,
  StatsSummary,
  AtlasDevice,
  Strategy
} from '../';
import {
  GRID_ROWS,
  GRID_COLS,
  DISABLED_CELLS,
  STORAGE_KEYS
} from '../../constants';
import {
  Idol,
  GridState
} from '../../types';
import {
  initializeEmptyGrid,
  updateGridWithRemovedItem,
  calculateTotalStats
} from '../../utils';
import useLocalStorage from '../../hooks/useLocalStorage';
import useDragAndDrop from '../../hooks/useDragAndDrop';
import ShareModal from '../../components/Share/ShareModal';
import ShareStateService from '../../services/ShareStateService';
import { parseIdolFromPaste } from '../../components/Idols/IdolPasteHandler';
import '../../App.css';
import IdolFormContainer from 'components/Idols/IdolForm/IdolFormContainer';
import StrategiesPage from '../../components/Strategy/StrategiesPage';
import FeaturedStrategiesPage from '../../components/Strategy/FeaturedStrategiesPage';
import SubmitStrategyPage from '../../components/Strategy/SubmitStrategyPage';
import { FEATURED_STRATEGIES, FEATURED_STRATEGIES_STORAGE_KEY } from '../../constants/FeaturedStrategies';
import { v4 as uuidv4 } from 'uuid';
import StashExplorerModal from '../../components/Stash/StashExplorerModal';

interface IdolPlannerProps {
  initialOpenStashExplorer?: boolean;
}

const IdolPlanner: React.FC<IdolPlannerProps> = ({ initialOpenStashExplorer = false }) => {

  // Use localStorage for persisting state
  const [placedItems, setPlacedItems] = useLocalStorage<Idol[]>(STORAGE_KEYS.PLACED_ITEMS, []);
  const [stashItems, setStashItems] = useLocalStorage<Idol[]>(STORAGE_KEYS.STASH_ITEMS, []);

  // Grid state management
  const [grid, setGrid] = useState<GridState>(initializeEmptyGrid(DISABLED_CELLS));

  // UI state management
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // Remove unused state variables by prefixing with underscore
  const [_isModBrowserOpen, _setIsModBrowserOpen] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [tooltipItem, setTooltipItem] = useState<Idol | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [stats, setStats] = useState<Record<string, number>>({});
  const [_wasStateLoaded, setWasStateLoaded] = useState<boolean>(false);

  // Add legacy notice state - persist in localStorage
  // Changed key to 'updateNoticeOpen_v2' to show notice again after 2.0 update
  const [isLegacyNoticeOpen, setIsLegacyNoticeOpen] = useLocalStorage<boolean>('updateNoticeOpen_v2', true);

  // Add mobile menu toggle state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  // Add notification state for successful pastes
  const [pasteNotification, setPasteNotification] = useState<string>('');

  const [isStrategyModalOpen, setIsStrategyModalOpen] = useState<boolean>(false);
  const [isFeaturedStrategiesModalOpen, setIsFeaturedStrategiesModalOpen] = useState<boolean>(false);
  const [isSubmitStrategyModalOpen, setIsSubmitStrategyModalOpen] = useState<boolean>(false);
  const [isStashExplorerModalOpen, setIsStashExplorerModalOpen] = useState<boolean>(false);

  

  // Drag and drop functionality
  const {
    draggedItem,
    hoverPosition,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    resetDrag
  } = useDragAndDrop();

  // Check for shared state on app load
  useEffect(() => {
    const hasLoadedState = ShareStateService.loadSharedState();
    if (hasLoadedState) {
      setWasStateLoaded(true);
      // After a short delay, reset the notification state
      setTimeout(() => {
        setWasStateLoaded(false);
      }, 5000);
    }
  }, []);

  // Initialize grid based on placed items
  useEffect(() => {
    // Create a grid where disabled cells and cells with items are marked
    const newGrid = initializeEmptyGrid(DISABLED_CELLS);

    placedItems.forEach(item => {
      if (item && item.position && item.size) {
        const { row, col } = item.position;
        const width = item.size.width;
        const height = item.size.height;

        for (let i = 0; i < height; i++) {
          for (let j = 0; j < width; j++) {
            if (row + i < GRID_ROWS && col + j < GRID_COLS) {
              newGrid[row + i][col + j].occupied = true;
            }
          }
        }
      }
    });

    setGrid(newGrid);
  }, [placedItems]);

  useEffect(() => {
    // Only set a timer if there's an active notification
    if (pasteNotification) {
      const timer = setTimeout(() => {
        setPasteNotification('');
      }, 3000);

      // Clean up function will run if component unmounts or if pasteNotification changes
      return () => clearTimeout(timer);
    }
  }, [pasteNotification]); // This effect runs when pasteNotification changes

  useEffect(() => {
    if (initialOpenStashExplorer) {
      setIsStashExplorerModalOpen(true);
    }
  }, [initialOpenStashExplorer]);

  // Calculate total stats
  useEffect(() => {
    const newStats = calculateTotalStats(placedItems);
    setStats(newStats);
  }, [placedItems]);

  // Track mouse position for tooltips
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isMobileMenuOpen && !(e.target as Element).closest('.mobile-menu-container')) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const handleEditIdol = useCallback((updatedIdol: Idol) => {
    const updatedItems = stashItems.map(item =>
      item.id === updatedIdol.id ? updatedIdol : item
    );
    setStashItems(updatedItems);
  }, [stashItems, setStashItems]);

  const _handleStashExplorerClick = () => {
    setIsStashExplorerModalOpen(true);
  };

  const handleAddIdol = useCallback((newIdol: Idol) => {
    // Fetch the current stash items directly from localStorage
    const currentStashItemsStr = localStorage.getItem(STORAGE_KEYS.STASH_ITEMS);
    let currentStashItems: Idol[] = [];

    if (currentStashItemsStr) {
      try {
        currentStashItems = JSON.parse(currentStashItemsStr);
      } catch (e) {
        console.error('Failed to parse stash items from localStorage', e);
      }
    }

    // Add the new idol
    const updatedStashItems = [...currentStashItems, newIdol];

    // Update localStorage directly 
    localStorage.setItem(STORAGE_KEYS.STASH_ITEMS, JSON.stringify(updatedStashItems));

    // Update state
    setStashItems(updatedStashItems);
  }, [setStashItems]); // Remove stashItems dependency

  // Add keyboard shortcut for pasting
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+V or Cmd+V outside of input fields - try to parse as idol
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        // Skip if focus is on an input element
        if (
          (e.target as HTMLElement).tagName === 'INPUT' ||
          (e.target as HTMLElement).tagName === 'TEXTAREA' ||
          (e.target as HTMLElement).isContentEditable
        ) {
          return;
        }

        // Prevent default only if we're not in an input field
        e.preventDefault();

        // Get clipboard content
        navigator.clipboard.readText().then(clipboardText => {
          // Try to parse an idol
          const idol = parseIdolFromPaste(clipboardText);

          if (idol) {
            // Successfully parsed - add to stash
            handleAddIdol(idol);

            // Show success notification
            showNotification(`Added ${idol.name} to stash!`);
          } else if (clipboardText.includes('Item Class:')) {
            // Looks like an item but couldn't parse automatically
            // Open the idol form modal
            setIsModalOpen(true);
          }
        }).catch(err => {
          console.error('Error accessing clipboard:', err);
        });
      }

      // Alt+Shift+I to open the idol form modal
      if (e.altKey && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        setIsModalOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleAddIdol]); // Added handleAddIdol to dependencies

  // Show notification function
  const showNotification = (message: string) => {
    setPasteNotification(message);
  };

  const handleMyStrategiesClick = () => {
    setIsStrategyModalOpen(true);
  };

  const handleFeaturedStrategiesClick = () => {
    setIsFeaturedStrategiesModalOpen(true);
  };

  const handleSubmitStrategyClick = () => {
    setIsSubmitStrategyModalOpen(true);
  };

  // Event handlers
  const handleDragOverEvent = useCallback((e: React.DragEvent<HTMLDivElement>, row: number, col: number) => {
    e.preventDefault();
    handleDragOver(row, col);
  }, [handleDragOver]);

  const handleDragEnterEvent = useCallback((e: React.DragEvent<HTMLDivElement>, row: number, col: number) => {
    e.preventDefault();
    handleDragEnter(row, col);
  }, [handleDragEnter]);

  const handleDragLeaveEvent = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleDragLeave();
  }, [handleDragLeave]);

  const handleDropEvent = useCallback((e: React.DragEvent<HTMLDivElement>, row: number, col: number) => {
    e.preventDefault();

    const result = handleDrop(row, col, grid);

    if (result.success && result.newGrid && result.newItem) {
      setGrid(result.newGrid);
      setPlacedItems([...placedItems, result.newItem]);
    }

    resetDrag();
  }, [grid, handleDrop, placedItems, resetDrag, setPlacedItems]);

  const handleDuplicateIdol = useCallback((idol: Idol) => {
    // Create a duplicate with a new unique ID
    const duplicatedIdol: Idol = {
      ...idol,
      id: `duplicate-${uuidv4()}`, // Generate a truly unique ID
      position: undefined // Reset position to avoid conflicts when dragging
    };

    // Add to stash items - get fresh value directly from localStorage
    const currentStashItemsStr = localStorage.getItem(STORAGE_KEYS.STASH_ITEMS);
    let currentStashItems: Idol[] = [];

    if (currentStashItemsStr) {
      try {
        currentStashItems = JSON.parse(currentStashItemsStr);
      } catch (e) {
        console.error('Failed to parse stash items from localStorage', e);
      }
    }

    // Add the duplicate idol
    const updatedStashItems = [...currentStashItems, duplicatedIdol];

    // Update localStorage directly 
    localStorage.setItem(STORAGE_KEYS.STASH_ITEMS, JSON.stringify(updatedStashItems));

    // Update state
    setStashItems(updatedStashItems);

    // Show success notification
    showNotification(`Duplicated ${idol.name}!`);
  }, [setStashItems]);

  const handleRemoveItem = useCallback((itemId: string) => {
    const itemToRemove = placedItems.find(i => i && i.id === itemId);

    if (itemToRemove) {
      const newGrid = updateGridWithRemovedItem(grid, itemToRemove);
      setGrid(newGrid);
      const updatedItems = placedItems.filter(i => i && i.id !== itemId);
      setPlacedItems(updatedItems);

      if (tooltipItem && tooltipItem.id === itemId) {
        setTooltipItem(null);
      }
    }
  }, [grid, placedItems, tooltipItem, setPlacedItems]);

  const handleRemoveStashItem = useCallback((itemId: string) => {
    const updatedStash = stashItems.filter(item => item && item.id !== itemId);
    setStashItems(updatedStash);
  }, [stashItems, setStashItems]);


  const handleClearGrid = useCallback(() => {
    setGrid(initializeEmptyGrid(DISABLED_CELLS));
    setPlacedItems([]);
    setTooltipItem(null);
  }, [setPlacedItems]);

  const handleItemMouseEnter = useCallback((item: Idol) => {
    setTooltipItem(item);
  }, []);

  const handleItemMouseLeave = useCallback(() => {
    setTooltipItem(null);
  }, []);

  // Reset all application state
  const handleResetAll = useCallback(() => {
    if (window.confirm('Are you sure you want to reset all data? This will clear all your saved setups and cannot be undone.')) {
      // Save strategies before reset
      const savedStrategiesJSON = localStorage.getItem('savedStrategies');
      const featuredStrategiesJSON = localStorage.getItem(FEATURED_STRATEGIES_STORAGE_KEY);

      // Clear all localStorage items
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });

      // Clear other known localStorage keys
      const otherKeys = [
        'atlasDeviceState',
        'atlasStrategy',
        'pinnedContentType',
        'pinnedContentTypes'
      ];

      otherKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      // Restore saved and featured strategies
      if (savedStrategiesJSON) {
        localStorage.setItem('savedStrategies', savedStrategiesJSON);
      }

      if (featuredStrategiesJSON) {
        localStorage.setItem(FEATURED_STRATEGIES_STORAGE_KEY, featuredStrategiesJSON);
      }

      // Reset state
      setGrid(initializeEmptyGrid(DISABLED_CELLS));
      setPlacedItems([]);
      setStashItems([]);
      setTooltipItem(null);

      // Force a page reload to ensure all components pick up the cleared state
      window.location.reload();
    }
  }, [setPlacedItems, setStashItems]);

  // Toggle mobile menu
  const toggleMobileMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-white relative pb-8">
      {/* Notifications */}
      {pasteNotification && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50 transition-opacity">
          {pasteNotification}
        </div>
      )}

      {/* Header with Navigation Menu */}
      <header className="bg-gray-800 shadow-md">
        <div className="container mx-auto px-4">
          {/* Main Header Row */}
          <div className="flex justify-between items-center py-4">
            <h1 className="text-xl font-bold">Path of Exile: Legacy of Phrecia - Idol and Strategy Planner</h1>

            {/* Hamburger menu for mobile */}
            <div className="lg:hidden">
              <button
                className="text-white focus:outline-none mobile-menu-button"
                onClick={toggleMobileMenu}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                </svg>
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex space-x-8 items-center">
              <button
                onClick={handleMyStrategiesClick}
                className="text-white hover:text-blue-300 transition-colors duration-200"
              >
                My Strategies
              </button>
              <button
                onClick={handleFeaturedStrategiesClick}
                className="text-white hover:text-blue-300 transition-colors duration-200 relative"
              >
                Featured Strategies
                <sup className="ml-1 text-xs bg-blue-500 text-white px-1 rounded-sm">{FEATURED_STRATEGIES.length}</sup>
              </button>
              <button
                onClick={handleSubmitStrategyClick}
                className="text-white hover:text-blue-300 transition-colors duration-200"
              >
                Submit Your Strategy
              </button>
              <a
                href="https://www.reddit.com/r/pathofexile/comments/1qmeont/my_phrecia_idol_and_strategy_planner_will_be/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white hover:text-blue-300 transition-colors duration-200"
              >
                Submit Feedback
              </a>
              {/* Hidden: In-Game Stash Explorer
              <button
                onClick={handleStashExplorerClick}
                className="text-white hover:text-blue-300 transition-colors duration-200 relative"
              >
                In-Game Stash Explorer
                <sup className="ml-1 text-xs bg-red-500 text-white px-1 rounded-sm">BETA</sup>
              </button>
              */}
              <BuyMeCoffeeButton />
            </nav>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`lg:hidden mobile-menu-container ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
          <nav className="bg-gray-700 px-4 py-2 shadow-inner">
            <ul className="space-y-2">
              <li>
                <button
                  onClick={handleMyStrategiesClick}
                  className="block w-full text-left py-2 px-4 text-white hover:bg-gray-600 rounded transition-colors duration-200"
                >
                  My Strategies
                </button>
              </li>
              <li>
                <button
                  onClick={handleFeaturedStrategiesClick}
                  className="block w-full text-left py-2 px-4 text-white hover:bg-gray-600 rounded transition-colors duration-200 relative"
                >
                  Featured Strategies
                  <sup className="ml-1 text-xs bg-blue-500 text-white px-1 rounded-sm">{FEATURED_STRATEGIES.length}</sup>
                </button>
              </li>
              <li>
                <button
                  onClick={handleSubmitStrategyClick}
                  className="block w-full text-left py-2 px-4 text-white hover:bg-gray-600 rounded transition-colors duration-200"
                >
                  Submit Your Strategy
                </button>
              </li>
              <li>
                <a
                  href="https://www.reddit.com/r/pathofexile/comments/1qmeont/my_phrecia_idol_and_strategy_planner_will_be/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-left py-2 px-4 text-white hover:bg-gray-600 rounded transition-colors duration-200"
                >
                  Submit Feedback
                </a>
              </li>
              {/* Hidden: In-Game Stash Explorer
              <li>
                <button
                  onClick={handleStashExplorerClick}
                  className="block w-full text-left py-2 px-4 text-white hover:bg-gray-600 rounded transition-colors duration-200 relative"
                >
                  In-Game Stash Explorer 
                   <sup className="ml-1 text-xs bg-red-500 text-white px-1 rounded-sm">BETA</sup>
                </button>
              </li>
              */}
              <li className="py-2 px-4 flex justify-start">
                <BuyMeCoffeeButton />
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Strategy Component - consistent margin */}
      <div className="bg-gray-800 p-4 mb-4">
        <Strategy />
      </div>

      {/* Main Content Area - Use align-items-center on large screens for vertical centering */}
      <div className="flex flex-wrap px-4 lg:items-start">
        {/* Left Column (Grid) - With self-center to enable vertical centering */}
        <div className="w-full lg:w-2/3 flex flex-col items-center mb-4 lg:pr-4 lg:self-center">
          <Grid
            grid={grid}
            placedItems={placedItems}
            draggedItem={draggedItem}
            hoverPosition={hoverPosition}
            tooltipItem={tooltipItem}
            mousePosition={mousePosition}
            onDragOver={handleDragOverEvent}
            onDragEnter={handleDragEnterEvent}
            onDragLeave={handleDragLeaveEvent}
            onDrop={handleDropEvent}
            onItemClick={handleRemoveItem}
            onMouseEnter={handleItemMouseEnter}
            onMouseLeave={handleItemMouseLeave}
            gridSizeMultiplier={1.5}
            onClearGrid={handleClearGrid}
          />
        </div>

        {/* Right Column (IdolStash) */}
        <div className="w-full lg:w-1/3 flex flex-col mb-4">
          <IdolStash
            stashItems={stashItems}
            onDragStart={handleDragStart}
            onRemoveItem={handleRemoveStashItem}
            onAddIdol={() => setIsModalOpen(true)}
            onEditIdol={handleEditIdol}
            onDuplicateIdol={handleDuplicateIdol}
          />
        </div>
      </div>

      {/* Bottom Row - Important: Changed from flex to flex-wrap */}
      <div className="flex flex-wrap px-4">
        {/* StatsSummary - Full width on small screens, 1/2 on large */}
        <div className="w-full md:w-1/2 mb-4 md:pr-2">
          <StatsSummary stats={stats} placedItems={placedItems} />
        </div>

        {/* AtlasDevice - Full width on small screens, 1/2 on large */}
        <div className="w-full md:w-1/2 mb-4 md:pl-2">
          <AtlasDevice />
        </div>
      </div>

      {/* Disclaimer Bar - fixed to bottom of screen, smaller size, responsive */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-500 text-gray-300 text-center py-1 text-xs md:text-xs shadow-lg z-40">
        <span className="block px-2">This product isn&apos;t affiliated with or endorsed by Grinding Gear Games in any way.</span>
      </div>

      {/* Legacy Notice - fixed to bottom left, collapsible */}
      <div className="fixed bottom-11 sm:bottom-8 left-2 sm:left-4 z-50 max-w-xs sm:max-w-sm">
        {isLegacyNoticeOpen ? (
          <div className="bg-blue-900/90 border-2 border-blue-600 rounded-lg shadow-lg">
            <div className="flex items-start justify-between p-3 sm:p-4">
              <div className="flex-1 pr-2">
                <h3 className="text-blue-400 font-semibold text-sm sm:text-base mb-2">Updated for Phrecia 2.0!</h3>
                <p className="text-blue-100 text-xs sm:text-sm leading-relaxed mb-3">
                  This site has been updated for <strong>Phrecia 2.0</strong> with the latest idol modifiers and scarabs!
                </p>
                <a
                  href="https://www.reddit.com/r/pathofexile/comments/1qmeont/my_phrecia_idol_and_strategy_planner_will_be/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-600 hover:bg-blue-500 text-white text-xs px-3 py-1.5 rounded transition-colors"
                >
                  Submit Feedback
                </a>
                {/* Legacy notice kept for future use:
                <h3 className="text-yellow-400 font-semibold text-sm sm:text-base mb-2">Legacy Tool Notice</h3>
                <p className="text-yellow-100 text-xs sm:text-sm leading-relaxed">
                  This tool was used during the <strong>Legacy of Phrecia League</strong> and is no longer actively supported. 
                  It is kept online to retain history and in case Idols return to the game in either Official or Private Leagues.
                </p>
                */}
              </div>
              <button
                onClick={() => setIsLegacyNoticeOpen(false)}
                className="text-blue-400 hover:text-blue-300 transition-colors flex-shrink-0 text-lg font-bold"
                aria-label="Close notice"
              >
                ×
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsLegacyNoticeOpen(true)}
            className="bg-blue-900/90 hover:bg-blue-800/90 border-2 border-blue-600 px-3 sm:px-4 py-2 rounded-lg transition-colors text-blue-400 text-xs sm:text-sm font-medium shadow-lg"
          >
            ℹ️ Update Notice
          </button>
        )}
      </div>

      {/* Action Buttons - standardized, responsive positioning */}
      <div className="fixed bottom-11 sm:bottom-8 right-2 sm:right-4 flex gap-1 sm:gap-3 z-50">
        <button
          className="bg-red-600 hover:bg-red-500 px-3 sm:px-4 py-2 sm:py-2 rounded transition-colors text-white text-sm sm:text-sm font-medium shadow-lg"
          onClick={handleResetAll}
        >
          Reset All
        </button>
        <button
          className="bg-blue-600 hover:bg-blue-500 px-3 sm:px-4 py-1 sm:py-2 rounded transition-colors text-white text-sm sm:text-sm font-medium shadow-lg"
          onClick={() => setIsShareModalOpen(true)}
        >
          Share
        </button>
      </div>

      {/* Modals */}
      <IdolFormContainer
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddIdol={handleAddIdol}
      />

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
      />



      <StrategiesPage
        isOpen={isStrategyModalOpen}
        onClose={() => setIsStrategyModalOpen(false)}
      />

      <FeaturedStrategiesPage
        isOpen={isFeaturedStrategiesModalOpen}
        onClose={() => setIsFeaturedStrategiesModalOpen(false)}
      />

      <SubmitStrategyPage
        isOpen={isSubmitStrategyModalOpen}
        onClose={() => setIsSubmitStrategyModalOpen(false)}
      />

      <StashExplorerModal
        isOpen={isStashExplorerModalOpen}
        onClose={() => setIsStashExplorerModalOpen(false)}
      />
    </div>
  );
};

export default IdolPlanner;